import FileSaver from 'file-saver';
import { unparse } from 'papaparse';
import { DateTimeFormat, getLocalDateTimeNow } from './dateFunctions';

export type CSVDataType = Record<string, string | Date | number | null | unknown>;

export function downloadCsv(data: CSVDataType[], filenamePrefix: string): void {
  const csv = unparse(data);
  const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(csvData, `${filenamePrefix} ${getLocalDateTimeNow(DateTimeFormat.Filename)}.csv`);
}

/**
 * Downloads a csv file using a string instead of an array of {@link CSVDataType}. The custom
 * string can be built using {@link buildCustomCsv} function which takes in an array of CSVDataType.
 */
export function downloadCustomCsv(csv: string, filenamePrefix: string): void {
  const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(csvData, `${filenamePrefix} ${getLocalDateTimeNow(DateTimeFormat.Filename)}.csv`);
}

export function buildCustomCsv(rows: CSVDataType[]): string {
  const lines: string[] = [];

  rows.forEach((row) => {
    const headers = Object.keys(row);
    const values = headers.map((key) => {
      const value = row[key];
      return !value ? '' : `"${String(value).replace(/"/g, '""')}"`;
    });
    lines.push(headers.join(','));
    if (values.length > 0) lines.push(values.join(','));
  });
  return lines.join('\n');
}

export function convertToCSVFormat<T>(data: T[], removeTimePart = false): CSVDataType[] {
  const csvData: CSVDataType[] = [];

  data.forEach((item: T) => {
    const converted: CSVDataType = {};
    for (const [key, value] of Object.entries(item)) {
      if (key === 'costBase') {
        continue; // Skip this key entirely
      }

      if (key === 'fees' && Array.isArray(value)) {
        const totalSum = value.reduce((sum, fee) => sum + (fee.total ?? 0), 0);
        converted[key] = totalSum;
      } else if (removeTimePart && typeof value === 'string' && !isNaN(Date.parse(value))) {
        converted[key] = value.split('T')[0];
      } else {
        converted[key] = value;
      }
    }

    csvData.push(converted);
  });

  return csvData;
}

export function downloadFile(fileData: Blob, fileName: string, fileType: string): void {
  FileSaver.saveAs(fileData, `${fileName}_${getLocalDateTimeNow(DateTimeFormat.Filename)}.${fileType}`);
}

export const extractFilenameFromContentDisposition = (contentDisposition: string): string | undefined => {
  if (!!contentDisposition) {
    try {
      let startIndex = contentDisposition.indexOf('filename="');
      let endIndex = contentDisposition.indexOf('"; filename*');
      if (startIndex > 0 && endIndex > 0) {
        return contentDisposition.substring(startIndex + 'filename="'.length, endIndex);
      }

      startIndex = contentDisposition.indexOf('filename=');
      endIndex = contentDisposition.indexOf('; filename*');
      if (startIndex > 0 && endIndex > 0) {
        return contentDisposition.substring(startIndex + 'filename='.length, endIndex);
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export function convertBase64StringToFile(base64String: string, fileName: string, fileType: string): File {
  const binStr = atob(base64String);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }

  return new File([arr], fileName, { type: fileType });
}
